/**
 * `persistentSpinData.ts` — web port of Unity
 * `Models/Data/PersistentSpinData.cs`.
 *
 * The persisted payload is a Unity-JsonUtility string stored VERBATIM in the
 * entity snapshot row's `PersistentSpinJson` (S) attribute (and mirrored on
 * the `update_entitysnapshot` wire as the snapshot's `persistentSpinJson`
 * field):
 *
 *   {"enabled":true,"speedDegreesPerSecond":60.0,"axis":"y"}
 *
 * Contract facts kept verbatim (`PersistentSpinData.cs`):
 *   - `Serialize` returns null (attribute omitted / cleared) when disabled or
 *     the speed is ~0 — "no spin" is the ABSENCE of the payload.
 *   - `Deserialize` returns null for blank/invalid/disabled/~0-speed JSON,
 *     normalizes NaN/Infinity speed to the 60 deg/s default, and coerces an
 *     unknown axis to "y".
 *   - Axis aliases (after trim + lowercase + stripping `_`/`-`/space):
 *     x|xaxis, y|yaxis|up|upaxis, z|zaxis|forward|forwardaxis.
 *   - Axis vectors are UNITY-space: x = right, y = up, z = forward.
 */

export const PERSISTENT_SPIN_DEFAULT_SPEED_DEG_PER_SEC = 60;

export type PersistentSpinAxis = "x" | "y" | "z";

export interface PersistentSpinData {
  readonly enabled: boolean;
  readonly speedDegreesPerSecond: number;
  readonly axis: PersistentSpinAxis;
}

/**
 * Unity `TryNormalizeAxis` — alias folding; `null` when the input is not a
 * recognized axis (the AI executor turns that into an explicit error).
 */
export function tryNormalizeSpinAxis(axis: unknown): PersistentSpinAxis | null {
  if (typeof axis !== "string") return null;
  const cleaned = axis.trim().toLowerCase().replace(/[-_ ]/g, "");
  if (cleaned === "x" || cleaned === "xaxis") return "x";
  if (cleaned === "y" || cleaned === "yaxis" || cleaned === "up" || cleaned === "upaxis") {
    return "y";
  }
  if (cleaned === "z" || cleaned === "zaxis" || cleaned === "forward" || cleaned === "forwardaxis") {
    return "z";
  }
  return null;
}

/** Unity `NormalizeAxis` — alias folding, unknown ⇒ "y". */
export function normalizeSpinAxis(axis: unknown): PersistentSpinAxis {
  return tryNormalizeSpinAxis(axis) ?? "y";
}

function normalizeSpeed(speed: unknown): number {
  const value = Number(speed);
  if (!Number.isFinite(value)) return PERSISTENT_SPIN_DEFAULT_SPEED_DEG_PER_SEC;
  return value;
}

/** Unity `HasActiveSpin`: enabled and speed not ~0. */
export function isActiveSpin(spin: PersistentSpinData | null): spin is PersistentSpinData {
  return !!spin && spin.enabled && Math.abs(spin.speedDegreesPerSecond) > 1e-4;
}

/** Unity `PersistentSpinData.Deserialize` — null for blank/invalid/inactive. */
export function parsePersistentSpinJson(json: string | null | undefined): PersistentSpinData | null {
  if (!json || !json.trim()) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const spin: PersistentSpinData = {
    enabled: record.enabled === true,
    speedDegreesPerSecond: normalizeSpeed(record.speedDegreesPerSecond),
    axis: normalizeSpinAxis(record.axis),
  };
  return isActiveSpin(spin) ? spin : null;
}

/**
 * Unity `PersistentSpinData.Serialize` — null when inactive (the caller
 * clears the attribute). Field order matches JsonUtility output.
 */
export function serializePersistentSpin(spin: PersistentSpinData | null): string | null {
  if (!isActiveSpin(spin)) return null;
  return JSON.stringify({
    enabled: true,
    speedDegreesPerSecond: spin.speedDegreesPerSecond,
    axis: spin.axis,
  });
}

/** Unity axis vector (UNITY space: x=right, y=up, z=forward). */
export function spinAxisVectorUnity(axis: PersistentSpinAxis): readonly [number, number, number] {
  switch (axis) {
    case "x":
      return [1, 0, 0];
    case "z":
      return [0, 0, 1];
    default:
      return [0, 1, 0];
  }
}

/**
 * Unity `GetContextSpeedLabel` — "60deg/s", "reverse 30deg/s", with an
 * " around x-axis"/" around z-axis" suffix when the axis isn't the default.
 * Used by the AI scene context's `spinning:{label}` indicator.
 */
export function spinContextSpeedLabel(spin: PersistentSpinData): string {
  const speed = Math.abs(spin.speedDegreesPerSecond);
  const digits = speed >= 1 ? 10 : 1000;
  const rounded = Math.round(speed * digits) / digits;
  const prefix = spin.speedDegreesPerSecond < 0 ? "reverse " : "";
  const suffix = spin.axis === "y" ? "" : ` around ${spin.axis}-axis`;
  return `${prefix}${rounded}deg/s${suffix}`;
}
